import { z } from 'zod';
import { FUNNEL_SCHEMA_VERSION } from '@/domain/funnel/schema-version';
import { isKnownEdgeType } from '@/domain/funnel/edge-system';
import type { EditorGraph, EditorNode } from './editor-graph';
import { getNodeDefinition } from '../nodes/registry/node-registry';
import { validateGraphEdges } from '../edges/domain/validate-graph';

const finiteNumber = z.number().finite();
const positionSchema = z.object({ x: finiteNumber, y: finiteNumber }).passthrough();

const nodeSchema = z
  .object({
    id: z.string().min(1),
    type: z.string().min(1).default('unknown'),
    position: positionSchema,
    data: z.record(z.unknown()).default({}),
    locked: z.boolean().default(false),
    width: finiteNumber.positive().optional(),
    height: finiteNumber.positive().optional(),
  })
  .passthrough();

const edgeSchema = z
  .object({
    id: z.string().min(1),
    source: z.string().min(1),
    target: z.string().min(1),
    type: z.string().min(1).optional(),
    sourceHandle: z.string().nullable().optional(),
    targetHandle: z.string().nullable().optional(),
    data: z.record(z.unknown()).default({}),
  })
  .passthrough();

const graphSchema = z
  .object({
    schemaVersion: z.number().int().positive(),
    nodes: z.array(nodeSchema).default([]),
    edges: z.array(edgeSchema).default([]),
    viewport: z
      .object({ x: finiteNumber, y: finiteNumber, zoom: finiteNumber.positive() })
      .default({ x: 0, y: 0, zoom: 1 }),
  })
  .passthrough();

export class InvalidEditorGraphError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidEditorGraphError';
  }
}

export interface GraphMigration {
  fromVersion: number;
  toVersion: number;
  migrate: (graph: Record<string, unknown>) => Record<string, unknown>;
}

// Version 1 is the first persisted graph schema, so there are no historical
// steps yet. Future versions append deterministic N -> N+1 migrations here.
export const GRAPH_MIGRATIONS: readonly GraphMigration[] = [];

export function migrateGraphCopy(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw;
  const initialVersion = (raw as { schemaVersion?: unknown }).schemaVersion;
  if (typeof initialVersion !== 'number' || !Number.isInteger(initialVersion)) {
    throw new InvalidEditorGraphError('Graph schemaVersion is missing or invalid.');
  }
  if (initialVersion > FUNNEL_SCHEMA_VERSION) {
    throw new InvalidEditorGraphError(
      `Graph schema version ${initialVersion} is not supported by this editor.`,
    );
  }
  let graph = structuredClone(raw) as Record<string, unknown>;
  let version = initialVersion;
  while (version < FUNNEL_SCHEMA_VERSION) {
    const migration = GRAPH_MIGRATIONS.find((candidate) => candidate.fromVersion === version);
    if (!migration || migration.toVersion !== version + 1) {
      throw new InvalidEditorGraphError(
        `No safe migration exists from graph schema version ${version}.`,
      );
    }
    graph = migration.migrate(graph);
    version = migration.toVersion;
    graph.schemaVersion = version;
  }
  return graph;
}

export function normalizeEditorGraph(raw: unknown): EditorGraph {
  const parsed = graphSchema.safeParse(migrateGraphCopy(raw));
  if (!parsed.success) {
    throw new InvalidEditorGraphError(
      'The funnel graph is invalid and could not be opened safely.',
    );
  }

  const nodeIds = new Set<string>();
  for (const node of parsed.data.nodes) {
    if (nodeIds.has(node.id)) throw new InvalidEditorGraphError(`Duplicate node ID: ${node.id}`);
    nodeIds.add(node.id);
  }

  // SPEC-006 §62/EDGE-REQ-039: dangling/self-loop/duplicate-ID edges are
  // excluded from the active graph (never rendered as connections) but
  // never crash the load — the raw funnel.graph_json from the API remains
  // the source of truth for recovery/analysis, this only affects what the
  // in-memory editor treats as a live connection.
  const { validEdges, warnings } = validateGraphEdges(nodeIds, parsed.data.edges);
  for (const warning of warnings) {
    console.warn(`[editor] excluded invalid edge ${warning.edgeId}: ${warning.reason}`);
  }

  return {
    ...parsed.data,
    nodes: parsed.data.nodes.map((node): EditorNode => {
      const definition = getNodeDefinition(node.type);
      return {
        ...node,
        type: definition?.type ?? 'fallback',
        data: definition
          ? {
              ...definition.defaultData,
              ...node.data,
              name:
                typeof node.data.name === 'string'
                  ? node.data.name
                  : typeof node.data.label === 'string'
                    ? node.data.label
                    : definition.defaultData.name,
              category: definition.category,
              icon: definition.icon,
              __editorLocked: node.locked,
            }
          : { ...node.data, __editorOriginalType: node.type, __editorLocked: node.locked },
        locked: node.locked,
        draggable: !node.locked,
        selectable: true,
        ...(definition?.type === 'group-frame' ? { zIndex: -1 } : {}),
      };
    }),
    edges: validEdges.map((edge) => {
      const originalType = edge.type ?? 'standard';
      return {
        ...edge,
        type: isKnownEdgeType(originalType) ? originalType : 'unknown',
        data: {
          style: 'solid',
          animated: false,
          ...edge.data,
          ...(isKnownEdgeType(originalType) ? {} : { __editorOriginalType: originalType }),
        },
      };
    }),
  };
}
